/**
 * SettingsDialog: 더보기 버튼 클릭 시 노출되는 설정 다이얼로그.
 * v1 원본: RenewalComponents/Footer/SettingsDialog/SettingsDialogContainer.js
 *         + SettingsDialogPresenter.js
 *
 * luna-rocket-common/LUXDialog → ReactDOM.createPortal + 동일 DOM 구조 수동 구현.
 * luna-rocket-common/LUXDialog는 inline-style 기반 modal wrapper 사용:
 *   ._isDialog.WSC_LUXDialog > dimmed overlay + table-cell center wrapper
 *
 * v1 SettingsDialogPresenter children 구조:
 *   dialog_content.rtc_dialog.rtc_new4_dialog.roundstyle > dialog_data > ...
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import InfoTab from './tabs/InfoTab';
import VideoTab from './tabs/VideoTab';
import AudioTab from './tabs/AudioTab';
import BackgroundTab from './tabs/BackgroundTab';
import MeetingMaterialTab from './tabs/MeetingMaterialTab';
import SettingsTab from './tabs/SettingsTab';
import TestTab from './tabs/TestTab';
import MeetingSettingsTab from './tabs/MeetingSettingsTab';
import type { MaterialListItem } from '@/api';
import { handleMaterialDocumentShare, getIsOpenWhiteboardSession } from '@/utils/whiteboard';
import LUXDialog from '@/components/Dialog';

interface SettingsDialogProps {
  onClose: () => void;
  initialMenu?: number;
  onChangeDevice?: (type: string, deviceId: string) => void;
}

const menuItems = [
  {
    label: '정보',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 7.5c.31 0 .563.252.563.563v4.124a.562.562 0 1 1-1.126 0V8.064c0-.311.252-.563.563-.563zM9 5.25a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5z" fill="#4a4a4a" />
        <path fillRule="evenodd" clipRule="evenodd" d="M9 1.5a7.5 7.5 0 1 1 0 15 7.5 7.5 0 0 1 0-15zm0 1.125a6.375 6.375 0 1 0 0 12.75 6.375 6.375 0 0 0 0-12.75z" fill="#4a4a4a" />
      </svg>
    ),
  },
  {
    label: '비디오',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.646 6.012a.75.75 0 1 1-.291 1.47.75.75 0 0 1 .291-1.47z" fill="#4A4A4A" />
        <path fillRule="evenodd" clipRule="evenodd" d="M11.662 3.563c1.222 0 2.213.99 2.213 2.212v1.213l1.744-.82.127-.05a1.013 1.013 0 0 1 1.316.966v3.832c0 .742-.772 1.232-1.443.916l-1.744-.82v1.213c0 1.222-.99 2.213-2.213 2.213h-7.95A2.213 2.213 0 0 1 1.5 12.224v-6.45c0-1.222.99-2.213 2.213-2.213h7.95zm-7.95 1.124c-.6 0-1.087.488-1.087 1.088v6.45c0 .6.487 1.088 1.088 1.088h7.95c.6 0 1.087-.488 1.087-1.088v-6.45c0-.6-.487-1.088-1.088-1.088h-7.95zm10.163 3.545v1.536l2.063.97V7.261l-2.063.97z" fill="#4A4A4A" />
      </svg>
    ),
  },
  {
    label: '오디오',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M9.324 2.788a.862.862 0 0 1 .227.048l.106.045.1.06c.063.045.12.098.17.157l.068.094.054.102a.86.86 0 0 1 .076.337V14.37a.862.862 0 0 1-1.35.695l-.004-.004-.005-.003-3.123-2.23H2.654A1.157 1.157 0 0 1 1.5 11.671V6.329l.006-.114a1.158 1.158 0 0 1 1.148-1.042h2.99l3.122-2.23.005-.003.004-.003a.864.864 0 0 1 .434-.15l.115.001zm-3.068 3.33-.001.001a.969.969 0 0 1-.56.18H2.658l-.023.01a.033.033 0 0 0-.01.022v5.338l.01.023c.006.006.014.01.022.01h3.038c.2 0 .396.062.56.178l.001.001L9 13.841V4.16l-2.744 1.96z" fill="#4A4A4A" />
        <path d="M13.942 4.746c.22-.22.576-.22.796 0a6.018 6.018 0 0 1 0 8.51.563.563 0 0 1-.796-.796 4.893 4.893 0 0 0 0-6.919.562.562 0 0 1 0-.795z" fill="#4A4A4A" />
        <path d="M11.79 6.149c.22-.22.576-.22.795 0a4.035 4.035 0 0 1 0 5.704.563.563 0 0 1-.795-.796 2.91 2.91 0 0 0 0-4.113.563.563 0 0 1 0-.795z" fill="#4A4A4A" />
      </svg>
    ),
  },
  {
    label: '배경',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.963 5.063a.975.975 0 1 1 0 1.95.975.975 0 0 1 0-1.95z" fill="#4A4A4A" />
        <path fillRule="evenodd" clipRule="evenodd" d="M13.537 2.25c1.222 0 2.213.99 2.213 2.213v9.074c0 1.222-.99 2.213-2.213 2.213H4.463a2.213 2.213 0 0 1-2.213-2.213V4.463c0-1.222.99-2.213 2.213-2.213h9.074zM3.375 12.619v.918c0 .601.487 1.088 1.088 1.088h9.074c.601 0 1.088-.487 1.088-1.088v-3.76l-2.535 2.519a.563.563 0 0 1-.792.001L7.606 8.655l-4.231 3.964zm1.088-9.244c-.601 0-1.088.487-1.088 1.088v6.613l3.858-3.612.088-.067a.563.563 0 0 1 .691.077l3.68 3.63 2.933-2.913V4.463c0-.601-.487-1.088-1.088-1.088H4.463z" fill="#4A4A4A" />
      </svg>
    ),
  },
  {
    label: '회의자료',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.125 10.754a.562.562 0 1 1 0 1.125h-8.25a.563.563 0 0 1 0-1.125h8.25zM9.972 7.756a.562.562 0 1 1 0 1.125H4.875a.563.563 0 0 1 0-1.125h5.097z" fill="#4A4A4A" />
        <path fillRule="evenodd" clipRule="evenodd" d="M7.095 2.438c.493 0 .961.215 1.282.59l1.172 1.369a.189.189 0 0 0 .143.066h4.595c1.222 0 2.213.99 2.213 2.212v6.675c0 1.222-.99 2.213-2.213 2.213H3.713A2.213 2.213 0 0 1 1.5 13.35v-8.7c0-1.222.99-2.213 2.213-2.213h3.382zM3.713 3.562c-.601 0-1.088.488-1.088 1.088v8.7c0 .6.487 1.088 1.088 1.088h10.574c.601 0 1.088-.488 1.088-1.088V6.675c0-.6-.487-1.087-1.088-1.087H9.692c-.383 0-.747-.168-.997-.46l-1.173-1.37a.563.563 0 0 0-.427-.196H3.713z" fill="#4A4A4A" />
      </svg>
    ),
  },
  {
    label: '설정',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M9 6a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm0 1.125a1.875 1.875 0 1 0 0 3.75 1.875 1.875 0 0 0 0-3.75z" fill="#4A4A4A" />
        <path fillRule="evenodd" clipRule="evenodd" d="M10.41 1.504c.202.017.397.092.559.219.162.127.284.3.346.497l.024.086.29 1.31c.298.138.583.299.854.481l1.342-.418.003-.002c.194-.059.402-.06.596-.005l.083.027.08.033c.158.075.295.187.398.33l.049.073v.001l1.322 2.214a1.012 1.012 0 0 1-.205 1.286l-.001.001-1.031.9c.013.152.021.306.022.46v.007c0 .153-.01.306-.022.459l1.03.9h.002a1.012 1.012 0 0 1 .206 1.286l-1.323 2.215a1.037 1.037 0 0 1-.527.437c-.218.082-.457.09-.679.021l-.003-.001-1.342-.419c-.271.183-.556.343-.854.481l-.29 1.31a1.024 1.024 0 0 1-.37.584 1.05 1.05 0 0 1-.646.223H7.677a1.05 1.05 0 0 1-.646-.223 1.022 1.022 0 0 1-.37-.583l-.291-1.31a6.177 6.177 0 0 1-.853-.482l-1.342.419-.003.002c-.222.067-.461.06-.68-.022a1.037 1.037 0 0 1-.526-.436v-.001L1.643 11.65a1.012 1.012 0 0 1 .207-1.287l1.03-.9a5.31 5.31 0 0 1 0-.927l-1.03-.899a1.012 1.012 0 0 1-.206-1.286l1.322-2.215c.122-.203.309-.356.527-.437l.083-.027c.166-.047.342-.053.512-.016l.084.021.003.002 1.342.418c.27-.182.556-.343.853-.481l.291-1.31c.052-.233.185-.438.37-.583l.071-.052c.171-.111.371-.17.575-.171h2.646l.087.004zM7.457 3.916l-.002.005a1.015 1.015 0 0 1-.582.7v.001c-.26.12-.51.26-.745.42a1.043 1.043 0 0 1-.906.143l-.003-.002-1.322-.413-1.24 2.077 1.185 1.035a.563.563 0 0 1 .186.506c-.06.406-.06.818 0 1.224a.563.563 0 0 1-.186.506l-1.186 1.034 1.241 2.077 1.322-.412.003-.002c.114-.035.233-.05.35-.044l.12.011.115.025c.112.031.22.08.316.147l.18.117c.183.114.374.217.57.307a1.032 1.032 0 0 1 .548.586l.034.115.002.005.286 1.291h2.514l.286-1.29.002-.006c.036-.157.11-.302.213-.425l.081-.086c.084-.08.182-.143.287-.19l.195-.095c.192-.098.378-.207.556-.329.13-.089.277-.148.431-.172l.119-.011c.118-.005.237.01.35.044l.004.002 1.321.412 1.24-2.077-1.184-1.034a.563.563 0 0 1-.187-.503 4.69 4.69 0 0 0 .045-.611 4.697 4.697 0 0 0-.045-.62.563.563 0 0 1 .187-.502l1.185-1.035-1.241-2.077-1.321.413-.003.002a1.052 1.052 0 0 1-.907-.143c-.235-.16-.485-.3-.745-.42a1.016 1.016 0 0 1-.582-.7v-.006l-.287-1.291H7.743l-.286 1.29z" fill="#4A4A4A" />
      </svg>
    ),
  },
  // {
  //   label: '테스트',
  //   icon: (
  //     <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
  //       <path fillRule="evenodd" clipRule="evenodd" d="M6.75 1.5a.563.563 0 0 1 .563.563V4.5h3.374V2.063a.563.563 0 0 1 1.126 0V4.5H12a.75.75 0 0 1 .53.22l.848.847a.75.75 0 0 1 .184.742l-1.5 5.25a.75.75 0 0 1-.494.494l-1.006.287v3.41a.563.563 0 0 1-1.125 0v-3.41l-1.006-.287a.75.75 0 0 1-.493-.494l-1.5-5.25a.75.75 0 0 1 .183-.742L7.47 4.72A.75.75 0 0 1 8 4.5h.188V2.063A.563.563 0 0 1 6.75 1.5zm1.72 4.125-.472.472L9.27 10.64 9 10.717l-.27-.076-1.272-4.541-.472-.475h3.028l-.472.475L9 6.576l.543-.476z" fill="#4A4A4A" />
  //       <path d="M3.375 14.625a.563.563 0 0 1 .563-.563h10.124a.563.563 0 0 1 0 1.125H3.938a.563.563 0 0 1-.563-.562z" fill="#4A4A4A" />
  //     </svg>
  //   ),
  // },
];

const SettingsDialog: React.FC<SettingsDialogProps> = ({ onClose, initialMenu = 0, onChangeDevice }) => {
  const [selectedMenu, setSelectedMenu] = useState(initialMenu);
  const [isEditMode, setIsEditMode] = useState(false);
  const [actionBtnText, setActionBtnText] = useState('문서 공유');
  const [actionBtnDisabled, setActionBtnDisabled] = useState(true);
  const actionBtnClickRef = useRef<(() => void) | null>(null);

  const handleStartDocumentShare = useCallback(
    async (material: MaterialListItem) => {
      if (!material.fileInfo) return;

      if (getIsOpenWhiteboardSession()) {
        LUXDialog.alert('이미 문서공유 또는 스케치가 진행중입니다.', {
          type: 'warning',
          useRenewalStyle: true,
        });
        return;
      }

      onClose();

      try {
        await handleMaterialDocumentShare({
          fileName: material.fileName,
          newFileName: material.fileInfo.newFileName,
          bucketType: material.fileInfo.bucketType,
        });
      } catch (err) {
        console.error('[MeetingMaterial] 문서공유 시작 실패:', err);
        LUXDialog.alert('문서 공유에 실패했습니다. 지원하지 않는 파일 형식이거나\n용량이 초과되었을 수 있습니다.', {
          type: 'warning',
          useRenewalStyle: true,
        });
      }
    },
    [onClose],
  );

  const handleActionBtnClick = useCallback(() => {
    actionBtnClickRef.current?.();
  }, []);

  // ESC 키 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    // body overflow 방지
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // dimmed 클릭 닫기
  const handleDimmedClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  const renderContent = () => {
    switch (selectedMenu) {
      case 0:
        return <InfoTab onEditMode={() => setIsEditMode(true)} />;
      case 1:
        return <VideoTab onChangeDevice={onChangeDevice} />;
      case 2:
        return <AudioTab onChangeDevice={onChangeDevice} />;
      case 3:
        return <BackgroundTab />;
      case 4:
        return (
          <MeetingMaterialTab
            setActionBtnText={setActionBtnText}
            setActionBtnDisabled={setActionBtnDisabled}
            onActionBtnClick={handleStartDocumentShare}
            actionBtnClickRef={actionBtnClickRef}
          />
        );
      case 5:
        return <SettingsTab />;
      case 6:
        return <TestTab />;
      default:
        return <InfoTab />;
    }
  };

  /**
   * luna-rocket-common/LUXDialog 또는 마크업 기준 동일 구조:
   * .LUX_basic_dialog
   *   > .dimmed
   *   > .dialog_wrap_tbl
   *     > .dialog_wrap_tblcel
   *       > .dialog_wrap (border:0, borderRadius:10px)
   *         > .dialog_content
   */
  const dialog = (
    <div className="rtc rtc_new3 LUX_basic_dialog" style={{ zIndex: 1502 }}>
      {/* dimmed */}
      <div className="dimmed" onClick={handleDimmedClick} />

      {/* center wrapper */}
      <div className="dialog_wrap_tbl">
        <div className="dialog_wrap_tblcel">
          {/* dialog box */}
          <div className="dialog_wrap" style={{ border: 0, borderRadius: '10px' }}>
            <div
              className="dialog_content rtc_dialog rtc_new4_dialog roundstyle"
              style={{ width: '740px' }}
            >
              <div className="dialog_data">
                <div className={`dialog_data_tit${isEditMode ? ' dialog_data_tit--rtcSet' : ''}`}>
                  {isEditMode && (
                    <button
                      type="button"
                      className="dialog_data_tit--rtcSetBtn"
                      onClick={() => setIsEditMode(false)}
                    >
                      <span className="lunacon lunacon--arrowLeft-outlined" />
                    </button>
                  )}
                  <h1 className={isEditMode ? '' : 'txtcnt'}>{isEditMode ? '회의 설정' : '설정'}</h1>
                  <button
                    type="button"
                    className="LUX_basic_btn btn_clr"
                    onClick={onClose}
                  >
                    <span className="sp_lux">닫기</span>
                  </button>
                </div>
                <div className="dialog_data_area rtcDialogSetup_dataArea">
                  <div className="rtcDialogSetupNew">
                    {/* 좌측 메뉴 — 수정 모드에서는 숨김 */}
                    {!isEditMode && (
                    <div className="rtcDialogSetupNew__left">
                      <ul className="rtcDialogSetupNew__list">
                        {menuItems.map((item, idx) => (
                          <li key={idx}>
                            <button
                              type="button"
                              className={`rtcDialogSetupNew__listBtn${selectedMenu === idx ? ' selected' : ''}`}
                              onClick={() => setSelectedMenu(idx)}
                            >
                              {item.icon}
                              <span>{item.label}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    )}

                    {/* 우측 콘텐츠 */}
                    <div
                      className={`rtcDialogSetupNew__right${isEditMode ? ' common_customScrollbar' : (selectedMenu === 3 || selectedMenu === 6) ? ' common_customScrollbar' : ''}`}
                    >
                      {isEditMode ? (
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <MeetingSettingsTab onBack={() => setIsEditMode(false)} />
                        </div>
                      ) : (
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          {renderContent()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, document.body);
};

export default SettingsDialog;
